import { LineItem } from '@commercetools/platform-sdk';
import {DrupalJsonApiParams} from 'drupal-jsonapi-params';
import striptags from 'striptags';
import {
  DrupalGuaranteeItems,
  FormatMessageFunction,
  GuaranteeInfo,
  GuaranteeInfoGuarantee,
  DrupalNode,
} from './types';

export const getGuaranteeInfo = ({
  guarantee,
}: {
  guarantee: GuaranteeInfoGuarantee;
  formatMessage: FormatMessageFunction;
}): GuaranteeInfo | null => {
  // The label is processed (safe) HTML from Drupal, but we just want the text.
  const label = striptags(guarantee.attributes.field_label.processed);
  return {
    icon: guarantee?.extra?.badge_svg_uri ?? '',
    title: label,
  };
};

/**
 * Generate a query string using DrupalJsonApiParams to fetch guarantee items
 * for each line item in the cart.
 *
 * @return {string} The query string to be used in the API request.
 */
export const getGuaranteeItemsQueryString = (
  lineItemsGuarantees: LineItem[],
): string => {
  const apiParams = new DrupalJsonApiParams();
  const productEntityField =
    'field_product.entity:commerce_product.field_product_key';

  // Create a filter group for OR condition.
  apiParams.addGroup('group1', 'OR');

  lineItemsGuarantees.forEach((lineItem) => {
    // Add conditions for each product key to the OR group.
    apiParams.addFilter(productEntityField, lineItem.productKey ?? '', '=', 'group1');
  });

  // Include these entity references.
  apiParams.addInclude([
    'field_guarantee_items',
    'field_product',
    'field_guarantee_items.field_media.field_media_image',
    'field_guarantee_items.field_media_reference.field_media_image',
  ]);

  return apiParams.getQueryString();
};

/**
 * Parse the guarantee items response from Drupal and map it to the line items.
 *
 * @param lineItems
 * @param guaranteeItemsResponse
 *
 * @return {DrupalNode[][]} An array of DrupalNodes (not optimal...see types.ts)
 */
export const parseGuaranteeItemsResponse = (
  lineItems: LineItem[],
  guaranteeItemsResponse: DrupalGuaranteeItems,
): DrupalNode[][] => lineItems.map((lineItem) => {
    // Find the product display node that matches the line item's product key.
    const productDisplayNode = guaranteeItemsResponse.data.find(
      (node) =>
        guaranteeItemsResponse.included.find(
          (rNode) =>
            rNode.attributes.field_product_key === lineItem.productKey &&
            rNode.id === node.relationships?.field_product?.data?.id,
        ) && node.relationships?.field_guarantee_items?.data?.length,
    );

    // For each guarantee item reference, find and return the referenced
    // guarantee item (also with an extra field for the badge SVG URL).
    return productDisplayNode?.relationships?.field_guarantee_items?.data.map(
      (node) => {
        const guaranteeItem = guaranteeItemsResponse.included.find(
          (rNode) => rNode.id === node.id,
        );
        if (guaranteeItem) {
          // Find the media image for the guarantee item. Note that we have
          // two possible media fields (one will be obsoleted in the future).
          const mid =
            guaranteeItem.relationships?.field_media.data.id ??
            guaranteeItem.relationships?.field_media_reference.data.id;
          const mediaItem = guaranteeItemsResponse.included.find(
            (el) => el.id === mid,
          );
          if (mediaItem) {
            // Now we need the file itself. Note that field_media_image is an
            // array.
            // @todo it probably shouldn't be, but we'd have to change in
            // Drupal.
            const fid = mediaItem.relationships?.field_media_image.data[0].id;
            const file = guaranteeItemsResponse.included.find(
              (el2) => el2.id === fid,
            );
            if (file) {
              const { url } = file.attributes.uri ?? '';
              guaranteeItem.extra = {
                badge_svg_uri: url,
              };
            }
          }
        }
        return guaranteeItem;
      },
    ) ?? [];
  }) as DrupalNode[][];
